<template>
  <Teleport
    to="#teleport-target-modal"
    tag="div"
  >
    <div
      :data-testid="$attrs ? $attrs['data-testid'] : ''"
      :class="[
        $attrs.staticClass,
        $attrs.class,
        {
          'scroll-lock': hasScrollLock,
          'com_modal--sticky-footer': footerIsSticky,
          'com_modal--sm': isSlim,
          'com_modal--v-align-top': isAlignedTop,
          'com_modal--confirm': isConfirm,

        },
      ].filter(Boolean)"
      class="com_modal"
    >
      <div
        ref="modal"
        tabindex="0"
        class="com_modal__inner-wrap"
        @keyup.esc="onEsc"
      >
        <div
          class="com_modal__flex-container"
        >
          <transition
            appear
            name="modal-fade"
            @after-leave="afterCloseAnimation"
          >
            <div
              v-if="isOpen"
              ref="modalWindow"
              class="com_modal__window"
            >
              <ButtonClose
                v-if="showClose && !isConfirm"
                size="3xl"
                mobile-size="xl"
                data-testid="modal-close-button"
                class="com_modal__x-close-button"
                @click="onClickClose"
              />

              <div
                data-testid="modal-content"
                class="com_modal__content"
              >
                <div
                  v-if="$slots.title"
                  class="com_modal__window-header"
                >
                  <div
                    class="com_modal__window-title"
                  >
                    <PfIcon
                      v-if="isConfirm"
                      name="attention"
                      size="lg"
                    />

                    <slot name="title" />
                  </div>

                  <div
                    v-if="$slots.actions"
                    class="com_modal__window-actions"
                  >
                    <slot name="actions" />
                  </div>
                </div>

                <slot />
              </div>
            </div>
          </transition>
        </div>
      </div>

      <transition
        appear
        name="darkener-fade"
      >
        <PfDarkener
          v-if="isOpen"
          @click="onClickOutside"
        />
      </transition>
    </div>
  </Teleport>

  <!-- eslint-disable-next-line vue/no-multiple-template-root -->
  <AlertModalConfirm
    v-if="showUnsavedChangesAlert"
    @on:discard="onDiscardChanges"
    @on:keep="onKeepChanges"
    @after:close-animation="afterCloseAnimation"
  />
</template>

<script>
import isOnLocalhost from '@client-shared/utils/is-on-localhost'
import { vOnClickOutside } from '@vueuse/components'

import AlertModalConfirm from '@/components/AlertModalConfirm.vue'
import ButtonClose from '@/components/ButtonClose.vue'
import PfDarkener from '@/components/PfDarkener.vue'
import PfIcon from '@/components/PfIcon.vue'
import generateComponentUid from '@/utils/generate-component-uid.js'

export default {
  name: 'PfModal',

  components: {
    AlertModalConfirm,
    PfDarkener,
    ButtonClose,
    PfIcon,
  },

  directives: {
    clickOutside: vOnClickOutside,
  },

  props: {
    isAlignedTop: {
      type: Boolean,
      default: false,
    },

    lockedIds: {
      type: Array,
      required: true,
    },

    showClose: {
      type: Boolean,
      default: true,
    },

    allowEscToClose: {
      type: Boolean,
      default: true,
    },

    hasChanges: {
      type: Boolean,
      default: false,
    },

    footerIsSticky: {
      type: Boolean,
      default: false,
    },

    isSlim: {
      type: Boolean,
      default: false,
    },

    isConfirm: {
      type: Boolean,
      default: false,
    },
  },

  emits: [ 'after:close-animation' ],

  expose: [
    'isModalOnTop',
    'close',
  ],

  data () {
    return {
      isOpen: true,
      preventClickOutside: false,
      uid: generateComponentUid(this),
      showUnsavedChangesAlert: false,
    }
  },

  computed: {
    openPopups () {
      return this.$store.state.layout.openPopups
    },

    matchedPopup () {
      return this.openPopups.find(popup => popup._id === this.uid)
    },

    hasScrollLock () {
      return this.matchedPopup && !this.isModalOnTop // Only add scroll-lock class if the modal is open, but not on top of the stack
    },

    isModalOnTop () {
      return this.$store.getters['layout/isPopupOnTop'](this.uid)
    },
  },

  watch: {
    lockedIds (newLockedIds) {
      this.$store.commit('layout/UPDATE_OPEN_POPUP_LOCKED_IDS', {
        popupId: this.uid,
        lockedIds: newLockedIds,
      })
    },

    '$route.path': {
      handler () {
        if (this.isOpen) {
          this.close()
        }
      },

      flush: 'sync',
    },
  },

  created () {
    this.$store.commit('layout/ADD_OPEN_POPUP', {
      popupId: this.uid,
      popupType: this.$config.constants.POPUP_TYPES.MODAL,
      lockedIds: this.lockedIds,
      component: this,
      componentName: this.$parent?.$options?.name || 'unknown', // Use parent name to get the name of the component which is using the modal
    })
  },

  mounted () {
    window.addEventListener('mousedown', this.onMouseDown)

    this.$nextTick(() => {
      if (this.$refs.modal) {
        this.$refs.modal.focus()
        this.$refs.modal.scrollTop = 0
      }
    })
  },

  beforeUnmount () {
    window.removeEventListener('mousedown', this.onMouseDown)

    if (this.isOpen) {
      console.warn('ATTENTION: Modal component is not unloaded properly. Use modal.close() before removing the modal from the DOM via v-if. Check ModalEditTaskAssignee integration as example implementation.')
    }
  },

  unmounted () {
    this.$store.commit('layout/REMOVE_OPEN_POPUP', this.uid)
    this.$socket.applyQueueUpdates()
  },

  methods: {
    // WARNING: close is also used from outside of the component
    close () {
      this.isOpen = false
    },

    onClickOutside (event) {
      // prevent modal from closing on outside click when click target on localhost is the vue dev tools bar
      if (isOnLocalhost && event.target.closest('#__vue-devtools-container__')) {
        return
      }

      if (!this.isModalOnTop) {
        return
      }

      if (this.preventClickOutside) {
        this.preventClickOutside = false

        return
      }

      this.onClickClose()
    },

    onClickClose () {
      if (document.getElementsByClassName('pintura-editor').length > 0) { // Hack to ensure, that the click-outside is not triggered if Pintura editor is open. We can't prevent the click-outside event to happen, because of the internal structure of the Pintura editor. It also ensures the correct behaviour of the "ESC" key handling if Pintura editor is open
        return
      }

      if (!this.showClose) {
        return
      }

      if (!this.didConfirmChanges()) {
        return
      }

      this.close()
    },

    didConfirmChanges () {
      if (!this.hasChanges) {
        return true
      }

      this.showUnsavedChangesAlert = true

      return false
    },

    onDiscardChanges () {
      this.close()
    },

    onKeepChanges () {
      this.showUnsavedChangesAlert = false
    },

    afterCloseAnimation () {
      this.$emit('after:close-animation')
    },

    onMouseDown (mouseEvent) {
      // Why onMouseDown handler to check if mouseUp happens outside of modal content?
      // When user triggers mousedown within modal content, but mouseup happens after moving the mouse outside of the modal, v-click-away triggers.
      // But whe have to block the v-click-away handler because the user does not want to close the modal in this special case
      // On touch devices this is not an issue as there is no mouse pointer which could be moved between mousedown and mouseup

      const modalMask = this.$refs.modalWindow

      // modalMask could be undefined because modal already unmounted before mouseup event fires
      if (modalMask) {
        const isClickInModalWindow = modalMask.contains(mouseEvent.target)

        this.preventClickOutside = isClickInModalWindow
      }
    },

    onEsc () {
      if (!this.allowEscToClose) {
        return
      }

      this.onClickClose()
    },
  },
}
</script>
